import {
    type AiAgentId,
    CONVERSATIONAL_MM_AGENT,
    getThreadErrorTypeDisplayName,
    SPROUT_AGENT,
} from '@fruitful/llm-service-shared';
import { getThreadErrorKey, type ThreadError } from '@fruitful/types';
import { router, useLocalSearchParams } from 'expo-router';
import { TabPanel, TabView } from 'primereact/tabview';
import { useMemo, useState } from 'react';
import {
    ArrowLeftIcon,
    BadgeAction,
    BadgeLabel,
    type BadgeLabelVariant,
    BodySmallRegular,
    ChevronLeftIcon,
    ChevronRightIcon,
    ExternalLinkIcon,
    isEmpty,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { PageContentContainer } from '../../common/components/GroveContainers';
import { LogRocketSessionsMenu } from '../../common/components/LogRocketSessionsMenu';
import {
    datadogLlmSessionUrl,
    mixpanelUserProfileUrl,
} from '../../common/utils/LinkUtils';
import { IS_PRODUCTION, IS_STAGING } from '../../config/FruitfulEnvironment';
import type { UserCategory } from '../../entities/entityTypes';
import { useEvalAgentThread } from '../hooks/useEvalAgentThread';
import { useListMastraThreads } from '../hooks/useListMastraThreads';
import { useListThreadScores } from '../hooks/useListThreadScores';
import { useThreadDetails } from '../hooks/useThreadDetails';
import { type ThreadConversationStatus } from '../utils/threadMetadataUtils';
import { AiThreadMemoryPanel } from './AiThreadMemoryPanel';
import { AiThreadMetricsPanel } from './AiThreadMetricsPanel';
import { AiThreadRawPanel } from './AiThreadRawPanel';
import { AiThreadResultsPanel } from './AiThreadResultsPanel';
import { AiThreadScoresPanel } from './AiThreadScoresPanel';
import { AiThreadTranscriptPanel } from './AiThreadTranscriptPanel';
import { ThreadFeedbackModal } from './ThreadFeedbackModal';
import { ThreadTracesPanel } from './ThreadTracesPanel';

const showExternalObservabilityLinks = IS_STAGING || IS_PRODUCTION;

const AiThreadDetailError = () => (
    <BodySmallRegular color="$red900" padding="$4">
        Thread ID and agent ID are required to view this thread.
    </BodySmallRegular>
);

const statusBadgeVariant = (
    status: ThreadConversationStatus,
): BadgeLabelVariant => {
    switch (status) {
        case 'Completed':
            return 'positive';
        case 'Active':
            return 'blue';
        case 'Churn':
            return 'warning';
        default: {
            const _exhaustive: never = status;
            return _exhaustive;
        }
    }
};

const navigateToMembership = (id: string | null) => {
    if (id == null || id === '') {
        return;
    }

    router.push({
        pathname: '/(app)/membership/[id]',
        params: { id },
    });
};

const navigateToAdjacentThread = ({
    threadId,
    agentId,
    selectedTabIndex,
}: {
    threadId: string;
    agentId: AiAgentId;
    selectedTabIndex?: string;
}) => {
    router.push({
        pathname: '/ai/threads/[threadId]',
        params: {
            threadId,
            agentId,
            ...(selectedTabIndex != null && selectedTabIndex !== ''
                ? { dt: selectedTabIndex }
                : {}),
        },
    });
};

const getThreadTitleLabelText = (title: string | null): string =>
    title ?? 'New chat';

const ThreadLabels = ({
    title,
    resourceId,
    visitorId,
    threadId,
    membershipId,
    userId,
    userCategory,
    status,
    errors,
    model,
    reasoningEffort,
    showStreamSelection = false,
    isRunningEval = false,
}: {
    title: string | null;
    resourceId: string | null;
    visitorId: string | null;
    threadId: string;
    membershipId: string | null;
    userId: string | null;
    userCategory: UserCategory | null;
    status: ThreadConversationStatus | null;
    errors: ThreadError[];
    model: string | null;
    reasoningEffort: string | null;
    showStreamSelection?: boolean;
    isRunningEval?: boolean;
}) => {
    return (
        <XStack
            alignItems="center"
            gap="$2"
            flexWrap="wrap"
            flex={1}
            flexBasis={0}
            minWidth={0}
            maxWidth="50%"
        >
            <BadgeLabel
                variant="neutral"
                text={`Title: ${getThreadTitleLabelText(title)}`}
            />
            {resourceId != null && resourceId !== '' ? (
                <BadgeLabel
                    variant="neutral"
                    text={`Resource Id: ${resourceId}`}
                />
            ) : null}
            {visitorId != null && visitorId !== '' ? (
                <BadgeLabel
                    variant="neutral"
                    text={`Visitor Id: ${visitorId}`}
                />
            ) : null}
            <BadgeLabel variant="neutral" text={`Thread Id: ${threadId}`} />
            {membershipId != null ? (
                <BadgeAction
                    variant="neutral"
                    text={`Membership Id: ${membershipId}`}
                    icon={ExternalLinkIcon}
                    onPress={() => {
                        navigateToMembership(membershipId);
                    }}
                />
            ) : null}
            {userId != null && membershipId != null ? (
                <BadgeAction
                    variant="neutral"
                    text={`Member Id: ${userId}`}
                    icon={ExternalLinkIcon}
                    onPress={() => {
                        navigateToMembership(membershipId);
                    }}
                />
            ) : null}
            {userId != null && membershipId == null ? (
                <BadgeLabel variant="neutral" text={`Member Id: ${userId}`} />
            ) : null}
            {userCategory != null ? (
                <BadgeLabel
                    variant="neutral"
                    text={`User Category: ${userCategory}`}
                />
            ) : null}
            {status != null ? (
                <BadgeLabel
                    variant={statusBadgeVariant(status)}
                    text={`Status: ${status}`}
                />
            ) : null}
            {errors.length > 0 ? (
                <XStack
                    alignItems="center"
                    gap="$2"
                    flexWrap="wrap"
                    opacity={isRunningEval ? 0.35 : 1}
                >
                    {errors.map((error) => (
                        <BadgeLabel
                            key={getThreadErrorKey(error)}
                            variant="danger"
                            text={getThreadErrorTypeDisplayName(error)}
                        />
                    ))}
                </XStack>
            ) : null}
            {showStreamSelection ? (
                <BadgeLabel variant="neutral" text={`Model: ${model ?? '—'}`} />
            ) : null}
            {showStreamSelection ? (
                <BadgeLabel
                    variant="neutral"
                    text={`Reasoning Effort: ${reasoningEffort ?? '—'}`}
                />
            ) : null}
        </XStack>
    );
};

const ThreadNavigator = ({
    previousThreadId,
    nextThreadId,
    positionLabel,
    onNavigateThread,
}: {
    previousThreadId: string | null;
    nextThreadId: string | null;
    positionLabel: string | null;
    onNavigateThread: (adjacentThreadId: string) => void;
}) => {
    return (
        <XStack alignItems="center" gap="$2" flexShrink={0} marginLeft="auto">
            <Button
                variant="secondary"
                size="small"
                icon={ChevronLeftIcon}
                disabled={previousThreadId == null}
                onPress={() => {
                    if (previousThreadId != null) {
                        onNavigateThread(previousThreadId);
                    }
                }}
            >
                Previous
            </Button>
            {positionLabel != null ? (
                <BodySmallRegular color="$gray1000">
                    {positionLabel}
                </BodySmallRegular>
            ) : null}
            <Button
                variant="secondary"
                size="small"
                icon={ChevronRightIcon}
                iconPosition="right"
                disabled={nextThreadId == null}
                onPress={() => {
                    if (nextThreadId != null) {
                        onNavigateThread(nextThreadId);
                    }
                }}
            >
                Next
            </Button>
        </XStack>
    );
};

const ThreadDetailNavigation = ({
    agentId,
    previousThreadId,
    nextThreadId,
    positionLabel,
    onNavigateThread,
}: {
    agentId: AiAgentId;
    previousThreadId: string | null;
    nextThreadId: string | null;
    positionLabel: string | null;
    onNavigateThread: (adjacentThreadId: string) => void;
}) => {
    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap="$2"
            flexWrap="wrap"
            paddingBottom="$3"
            borderBottomWidth={1}
            borderBottomColor="$gray300"
        >
            <Button
                variant="tertiary"
                size="small"
                icon={ArrowLeftIcon}
                href={{
                    pathname: '/ai',
                    params: { agentId },
                }}
            >
                Back to Dashboard
            </Button>
            <ThreadNavigator
                previousThreadId={previousThreadId}
                nextThreadId={nextThreadId}
                positionLabel={positionLabel}
                onNavigateThread={onNavigateThread}
            />
        </XStack>
    );
};

const ThreadActions = ({
    threadId,
    logRocketUserId,
    mixpanelUserId,
    isRunningEval,
    runEvalLabel,
    onRunEval,
    onGiveFeedback,
}: {
    threadId: string;
    logRocketUserId: string | null;
    mixpanelUserId: string | null;
    isRunningEval: boolean;
    runEvalLabel: string;
    onRunEval: () => void;
    onGiveFeedback: () => void;
}) => {
    const hasMixpanelUserId = !isEmpty(mixpanelUserId);

    return (
        <XStack alignItems="center" gap="$2" flexShrink={0} marginLeft="auto">
            {showExternalObservabilityLinks ? (
                <>
                    <Button
                        variant="secondary"
                        size="small"
                        icon={ExternalLinkIcon}
                        iconPosition="right"
                        onPress={() => {
                            window.open(
                                datadogLlmSessionUrl({
                                    sessionId: threadId,
                                    environment: IS_PRODUCTION
                                        ? 'production'
                                        : 'staging',
                                }),
                                '_blank',
                            );
                        }}
                    >
                        Datadog
                    </Button>
                    <Button
                        variant="secondary"
                        size="small"
                        icon={ExternalLinkIcon}
                        iconPosition="right"
                        disabled={!hasMixpanelUserId}
                        onPress={() => {
                            if (!hasMixpanelUserId) {
                                return;
                            }
                            window.open(
                                mixpanelUserProfileUrl(mixpanelUserId ?? ''),
                                '_blank',
                            );
                        }}
                    >
                        Mixpanel
                    </Button>
                    {logRocketUserId != null && logRocketUserId !== '' ? (
                        <LogRocketSessionsMenu userId={logRocketUserId} />
                    ) : (
                        <Button
                            variant="secondary"
                            size="small"
                            icon={ExternalLinkIcon}
                            iconPosition="right"
                            disabled
                        >
                            LogRocket
                        </Button>
                    )}
                </>
            ) : null}
            <Button variant="secondary" size="small" onPress={onGiveFeedback}>
                Give feedback
            </Button>
            <Button
                variant="primary"
                size="small"
                disabled={isRunningEval}
                icon={isRunningEval ? <Spinner /> : undefined}
                onPress={onRunEval}
            >
                {runEvalLabel}
            </Button>
        </XStack>
    );
};

/** Agent-agnostic detail view for a single thread. */
export const AiThreadDetailPage = () => {
    const {
        threadId,
        agentId,
        dt: selectedTabIndex,
    } = useLocalSearchParams<{
        threadId?: string;
        agentId?: AiAgentId;
        dt?: string;
    }>();

    const { mutate: runEval, isPending: isRunningEval } = useEvalAgentThread();
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const { data: scores = [] } = useListThreadScores({
        threadId,
        enabled: threadId != null && threadId !== '',
    });
    const hasScores = scores.length > 0;
    const runEvalLabel = hasScores ? 'Re-run eval' : 'Run eval';
    const {
        title,
        resourceId,
        status,
        errors,
        visitorId,
        userId,
        membershipId,
        userCategory,
        logRocketUserId,
        mixpanelUserId,
        model,
        reasoningEffort,
    } = useThreadDetails({
        threadId,
        agentId,
        isRunningEval,
    });

    // Same query/order as the AI dashboard default list (updatedAt DESC).
    // TODO: client-side table filters/sort on the dashboard are not preserved here yet.
    const { data: threads = [] } = useListMastraThreads({
        agentId: agentId ?? '',
        enabled: agentId != null,
    });

    const threadNavigation = useMemo(() => {
        if (threadId == null || threadId === '') {
            return {
                previousThreadId: null,
                nextThreadId: null,
                positionLabel: null,
            };
        }

        const currentIndex = threads.findIndex(
            (thread) => thread.id === threadId,
        );
        if (currentIndex < 0) {
            return {
                previousThreadId: null,
                nextThreadId: null,
                positionLabel: null,
            };
        }

        return {
            previousThreadId: threads[currentIndex - 1]?.id ?? null,
            nextThreadId: threads[currentIndex + 1]?.id ?? null,
            positionLabel: `${(currentIndex + 1).toString()} / ${threads.length.toString()}`,
        };
    }, [threadId, threads]);

    if (threadId == null || threadId === '' || agentId == null) {
        return (
            <YStack width="94%" marginVertical="3vh">
                <PageContentContainer paddingTop={16}>
                    <AiThreadDetailError />
                </PageContentContainer>
            </YStack>
        );
    }

    return (
        <YStack width="98%">
            <PageContentContainer>
                <YStack gap="$4" width="100%">
                    <ThreadDetailNavigation
                        agentId={agentId}
                        previousThreadId={threadNavigation.previousThreadId}
                        nextThreadId={threadNavigation.nextThreadId}
                        positionLabel={threadNavigation.positionLabel}
                        onNavigateThread={(adjacentThreadId) => {
                            navigateToAdjacentThread({
                                threadId: adjacentThreadId,
                                agentId,
                                selectedTabIndex,
                            });
                        }}
                    />
                    <XStack
                        alignItems="flex-start"
                        width="100%"
                        gap="$3"
                        paddingBottom="$3"
                        borderBottomWidth={1}
                        borderBottomColor="$gray300"
                    >
                        <ThreadLabels
                            title={title}
                            resourceId={resourceId}
                            visitorId={visitorId}
                            threadId={threadId}
                            membershipId={membershipId}
                            userId={userId}
                            userCategory={userCategory}
                            status={status}
                            errors={errors}
                            model={model}
                            reasoningEffort={reasoningEffort}
                            showStreamSelection={agentId === SPROUT_AGENT.id}
                            isRunningEval={isRunningEval}
                        />
                        <ThreadActions
                            threadId={threadId}
                            logRocketUserId={logRocketUserId}
                            mixpanelUserId={mixpanelUserId}
                            isRunningEval={isRunningEval}
                            runEvalLabel={runEvalLabel}
                            onRunEval={() => {
                                runEval({ agentId, threadId });
                            }}
                            onGiveFeedback={() => {
                                setIsFeedbackOpen(true);
                            }}
                        />
                    </XStack>
                    <ThreadFeedbackModal
                        isOpen={isFeedbackOpen}
                        onClose={() => {
                            setIsFeedbackOpen(false);
                        }}
                        threadId={threadId}
                        agentId={agentId}
                        scores={scores}
                        errors={errors}
                    />
                    <XStack
                        alignItems="stretch"
                        gap="$5"
                        width="100%"
                        minHeight="70vh"
                    >
                        <YStack flex={1} minWidth={0} width="100%">
                            <TabView
                                style={{ width: '100%' }}
                                panelContainerStyle={{
                                    width: '100%',
                                    padding: 0,
                                    marginTop: 32,
                                }}
                                activeIndex={
                                    parseInt(selectedTabIndex ?? '', 10) || 0
                                }
                                onTabChange={(event) => {
                                    router.setParams({
                                        dt: event.index.toString(),
                                    });
                                }}
                            >
                                <TabPanel header="Transcript">
                                    <AiThreadTranscriptPanel
                                        threadId={threadId}
                                        agentId={agentId}
                                        memberUserId={userId}
                                    />
                                </TabPanel>

                                <TabPanel header="Raw">
                                    <AiThreadRawPanel
                                        threadId={threadId}
                                        agentId={agentId}
                                    />
                                </TabPanel>

                                {agentId === SPROUT_AGENT.id ? (
                                    <TabPanel header="Memory">
                                        <AiThreadMemoryPanel
                                            threadId={threadId}
                                            agentId={agentId}
                                        />
                                    </TabPanel>
                                ) : null}

                                {agentId === CONVERSATIONAL_MM_AGENT.id ? (
                                    <TabPanel header="Results">
                                        <AiThreadResultsPanel
                                            agentId={agentId}
                                            mastraThreadId={threadId}
                                        />
                                    </TabPanel>
                                ) : null}

                                <TabPanel header="Tracing">
                                    <ThreadTracesPanel threadId={threadId} />
                                </TabPanel>
                            </TabView>
                        </YStack>
                        <YStack
                            width={380}
                            flexShrink={0}
                            paddingLeft="$4"
                            borderLeftWidth={1}
                            borderLeftColor="$gray300"
                        >
                            <AiThreadMetricsPanel
                                threadId={threadId}
                                agentId={agentId}
                                isRunningEval={isRunningEval}
                            />
                        </YStack>
                        <YStack
                            width={380}
                            flexShrink={0}
                            paddingLeft="$4"
                            borderLeftWidth={1}
                            borderLeftColor="$gray300"
                        >
                            <AiThreadScoresPanel
                                threadId={threadId}
                                agentId={agentId}
                                isRunningEval={isRunningEval}
                            />
                        </YStack>
                    </XStack>
                </YStack>
            </PageContentContainer>
        </YStack>
    );
};
